// ============================================================================
// FILE: circuitValidation.js
// Backend circuit validation using hooks
// ============================================================================
// client/src/validations/circuitValidation.js

import {
  parseValidationResponse,
  createErrorResponse,
  handleApiError
} from './validationHelpers';

/**
 * Parse circuit validation response from backend
 * Use this after calling useVisualCircuit().validateCircuit()
 * 
 * @param {Object} backendResponse - Response from validateCircuit hook
 * @returns {ValidationResponse} Standardized validation response
 */
export function parseCircuitValidation(backendResponse) {
  try {
    console.log('🔍 Parsing circuit validation response');

    if (!backendResponse) {
      return createErrorResponse('No validation response received');
    }

    // Parse using standard helper
    const parsed = parseValidationResponse(backendResponse);

    console.log('✅ Circuit validation parsed:', {
      valid: parsed.valid,
      errors: parsed.errors.length,
      warnings: parsed.warnings.length
    });

    return parsed;

  } catch (error) {
    console.error('❌ Error parsing circuit validation:', error);
    return handleApiError(error, 'Circuit validation parsing');
  }
}

// ============================================================================
// FILTER HELPERS - Extract specific validation categories
// ============================================================================

/**
 * Filter connection-related errors and warnings
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterConnectionValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  // ✅ Protocol pin names - these belong to protocols tab, not connections tab
  const protocolPins = ['sda', 'scl', 'mosi', 'miso', 'sck', 'cs', 'tx', 'rx',
                      'adc2', 'non-default', 'strapping', 'sccb', 'pin conflict'];
  
  // ✅ Configuration keywords - these belong to configuration tab
  const configKeywords = [
    'no configured software pins',
    'protocol is not enabled',
    'conflict with mqtt client',
    'conflict with',
    'both create separate cloud connections',
    'mqtt client',
    'azure iot hub'
  ];

  const connectionErrors = validationResult.errors.filter(error => {
    const msg = error.message?.toLowerCase();
    
    // ✅ Exclude if it's a protocol pin
    const isProtocolPin = protocolPins.some(pin => msg?.includes(pin));
    if (isProtocolPin) return false;
    
    // ✅ Exclude if it's a configuration issue
    const isConfiguration = configKeywords.some(keyword => msg?.includes(keyword));
    if (isConfiguration) return false;
    
    return (
      error.category === 'connection' ||
      error.connection ||
      msg?.includes('pin') ||
      msg?.includes('connect')
    );
  });

  const connectionWarnings = validationResult.warnings.filter(warning => {
    const msg = warning.message?.toLowerCase();
    
    // ✅ Exclude if it's a protocol pin
    const isProtocolPin = protocolPins.some(pin => msg?.includes(pin));
    if (isProtocolPin) return false;

    // ✅ Exclude if it's a configuration issue
    const isConfiguration = configKeywords.some(keyword => msg?.includes(keyword));
    if (isConfiguration) return false;

    return (
      warning.category === 'connection' ||
      warning.connection ||
      msg?.includes('pin') ||
      msg?.includes('connect')
    );
  });

  return {
    success: validationResult.success,
    valid: connectionErrors.length === 0,
    errors: connectionErrors,
    warnings: connectionWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'connections'
    }
  };
}

/**
 * Filter protocol-related errors and warnings (I2C, SPI, UART)
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterProtocolValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const protocolErrors = validationResult.errors.filter(error => 
    error.category === 'protocol' ||
    error.message?.toLowerCase().includes('i2c') ||
    error.message?.toLowerCase().includes('spi') ||
    error.message?.toLowerCase().includes('uart') ||
    error.message?.toLowerCase().includes('sda') ||
    error.message?.toLowerCase().includes('scl') ||
    error.message?.toLowerCase().includes('mosi') ||
    error.message?.toLowerCase().includes('miso') ||
    error.message?.toLowerCase().includes('adc2') ||
    error.message?.toLowerCase().includes('non-default') ||
    error.message?.toLowerCase().includes('strapping') ||
    error.message?.toLowerCase().includes('sccb') ||
    error.message?.toLowerCase().includes('pin conflict')
  );

  const protocolWarnings = validationResult.warnings.filter(warning => 
    warning.category === 'protocol' ||
    warning.message?.toLowerCase().includes('i2c') ||
    warning.message?.toLowerCase().includes('spi') ||
    warning.message?.toLowerCase().includes('uart') ||
    warning.message?.toLowerCase().includes('adc2') ||
    warning.message?.toLowerCase().includes('non-default') ||
    warning.message?.toLowerCase().includes('strapping') ||
    warning.message?.toLowerCase().includes('sccb') ||
    warning.message?.toLowerCase().includes('pin conflict')
  );

  return {
    success: validationResult.success,
    valid: protocolErrors.length === 0,
    errors: protocolErrors,
    warnings: protocolWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'protocols'
    }
  };
}

/**
 * Filter I2C address conflict errors
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterI2CAddressValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const i2cErrors = validationResult.errors.filter(error => 
    error.category === 'i2c' ||
    error.message?.toLowerCase().includes('i2c address') ||
    error.message?.toLowerCase().includes('address conflict') ||
    error.message?.toLowerCase().includes('duplicate address')
  );

  const i2cWarnings = validationResult.warnings.filter(warning => 
    warning.category === 'i2c' ||
    warning.message?.toLowerCase().includes('i2c address')
  );

  return {
    success: validationResult.success,
    valid: i2cErrors.length === 0,
    errors: i2cErrors,
    warnings: i2cWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'i2c_addresses'
    }
  };
}

/**
 * Filter component configuration errors (pins not configured, protocols not enabled, etc.)
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterConfigurationValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const configErrors = validationResult.errors.filter(error => {
    const msg = error.message?.toLowerCase();
    return (
      error.category === 'configuration' ||
      msg?.includes('no configured software pins') ||
      msg?.includes('protocol is not enabled') ||
      msg?.includes('is not enabled') ||
      msg?.includes('conflict with mqtt client') ||
      msg?.includes('conflict with') ||
      msg?.includes('both create separate cloud connections') ||
      msg?.includes('not enabled in component configuration')
    );
  });

  const configWarnings = validationResult.warnings.filter(warning => {
    const msg = warning.message?.toLowerCase();
    return (
      warning.category === 'configuration' ||
      msg?.includes('no configured software pins') ||
      msg?.includes('protocol is not enabled') ||
      msg?.includes('conflict with mqtt client') ||
      msg?.includes('conflict with') ||
      msg?.includes('both create separate cloud connections') ||
      msg?.includes('not enabled in component configuration')
    );
  });

  return {
    success: validationResult.success,
    valid: configErrors.length === 0,
    errors: configErrors,
    warnings: configWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'configuration'
    }
  };
}

/**
 * Filter cross-component dependency errors (WiFi for MQTT, etc.)
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterDependencyValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const dependencyErrors = validationResult.errors.filter(error => {
    const msg = error.message?.toLowerCase();
    if (msg?.includes('adc2')) return false; // belongs to protocols
    return (
      error.category === 'dependency' ||
      msg?.includes('requires') ||
      msg?.includes('wifi') ||
      msg?.includes('depends on')
    );
  });

  const dependencyWarnings = validationResult.warnings.filter(warning => {
    const msg = warning.message?.toLowerCase();
    if (msg?.includes('adc2')) return false; // belongs to protocols
    return (
      warning.category === 'dependency' ||
      msg?.includes('requires') ||
      msg?.includes('wifi') ||
      msg?.includes('communication')
    );
  });

  return {
    success: validationResult.success,
    valid: dependencyErrors.length === 0,
    errors: dependencyErrors,
    warnings: dependencyWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'dependencies'
    }
  };
}

/**
 * Filter pin restriction errors (strapping pins, input-only, etc.)
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterPinRestrictionValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const restrictionErrors = validationResult.errors.filter(error => 
    error.category === 'pin_restriction' ||
    error.message?.toLowerCase().includes('strapping') ||
    error.message?.toLowerCase().includes('input-only') ||
    error.message?.toLowerCase().includes('input only') ||
    error.message?.toLowerCase().includes('restriction')
  );

  const restrictionWarnings = validationResult.warnings.filter(warning => 
    warning.category === 'pin_restriction' ||
    warning.message?.toLowerCase().includes('strapping') ||
    warning.message?.toLowerCase().includes('input-only')
  );

  return {
    success: validationResult.success,
    valid: restrictionErrors.length === 0,
    errors: restrictionErrors,
    warnings: restrictionWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'pin_restrictions'
    }
  };
}

/**
 * Get only unconnected pin warnings
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterUnconnectedPins(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const unconnectedWarnings = validationResult.warnings.filter(warning => 
    warning.message?.toLowerCase().includes('not connected') ||
    warning.message?.toLowerCase().includes('unconnected')
  );

  return {
    success: validationResult.success,
    valid: true, // Unconnected pins are warnings, not errors
    errors: [],
    warnings: unconnectedWarnings,
    metadata: {
      ...validationResult.metadata,
      hasUnconnectedPins: unconnectedWarnings.length > 0,
      filteredBy: 'unconnected_pins'
    }
  };
}

export function filterPipelineValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const pipelineErrors = validationResult.errors.filter(error =>
    error.category === 'pipeline' ||           // ← new: backend category
    error.message?.toLowerCase().includes('pipeline') ||
    error.message?.toLowerCase().includes('stage') ||
    error.message?.toLowerCase().includes('direction') ||
    error.message?.toLowerCase().includes('skipping') ||
    error.message?.toLowerCase().includes('invalid pipeline') ||
    error.message?.toLowerCase().includes('cannot connect two')
  );

  const pipelineWarnings = validationResult.warnings.filter(warning =>
    warning.category === 'pipeline' ||         // ← new: backend category
    warning.message?.toLowerCase().includes('pipeline') ||
    warning.message?.toLowerCase().includes('stage') ||
    warning.message?.toLowerCase().includes('skip')
  );

  return {
    success: validationResult.success,
    valid: pipelineErrors.length === 0,
    errors: pipelineErrors,
    warnings: pipelineWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'pipeline'
    }
  };
}

/**
 * Filter LangGraph-specific errors and warnings
 * Used by the AgenticAI 'Graph Wiring' validation tab.
 * Matches errors with category === 'graph' or graph-related keywords.
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterGraphValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const graphKeywords = [
    'graph',
    'graph_nodes',
    'graph_edges',
    'langgraph',
    'agent state',
    'stategraph',
    'conditional edge',
    'graph runner',
    'supervisor node',
    'tutor node',
    'retriever node',
    'guard node',
    'graph sub-canvas',
    'branch'
  ];

  const graphErrors = validationResult.errors.filter(error => {
    const msg = error.message?.toLowerCase();
    return (
      error.category === 'graph' ||
      graphKeywords.some(kw => msg?.includes(kw))
    );
  });

  const graphWarnings = validationResult.warnings.filter(warning => {
    const msg = warning.message?.toLowerCase();
    return (
      warning.category === 'graph' ||
      graphKeywords.some(kw => msg?.includes(kw))
    );
  });

  return {
    success: validationResult.success,
    valid: graphErrors.length === 0,
    errors: graphErrors,
    warnings: graphWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'graph'
    }
  };
}


// ============================================================================
// COMPREHENSIVE VALIDATION
// ============================================================================

/**
 * Parse and categorize all circuit validation results
 * Call this after getting validation response from hook
 * 
 * @param {Object} validationResponse - Response from useVisualCircuit().validateCircuit()
 * @returns {Object} Categorized validation results
 */
export function parseComprehensiveCircuitValidation(validationResponse) {
  try {
    // Parse main validation
    const mainResult = parseCircuitValidation(validationResponse);

    if (!mainResult.success) {
      return {
        success: false,
        valid: false,
        overall: mainResult,
        byCategory: {}
      };
    }

    // Filter by categories
    const categorized = {
      connections: filterConnectionValidation(mainResult),
      protocols: filterProtocolValidation(mainResult),
      i2cAddresses: filterI2CAddressValidation(mainResult),
      dependencies: filterDependencyValidation(mainResult),
      pinRestrictions: filterPinRestrictionValidation(mainResult),
      unconnectedPins: filterUnconnectedPins(mainResult),
      pipeline: filterPipelineValidation(mainResult),
      graph:    filterGraphValidation(mainResult),
    };

    return {
      success: true,
      valid: mainResult.valid,
      overall: mainResult,
      byCategory: categorized
    };

  } catch (error) {
    console.error('❌ Error in comprehensive validation:', error);
    return {
      success: false,
      valid: false,
      overall: handleApiError(error, 'Comprehensive validation'),
      byCategory: {}
    };
  }
}

// ============================================================================
// EXPORT ALL FUNCTIONS
// ============================================================================

export default {
  // Main parser
  parseCircuitValidation,
  
  // Category filters
  filterConnectionValidation,
  filterProtocolValidation,
  filterI2CAddressValidation,
  filterDependencyValidation,
  filterPinRestrictionValidation,
  filterUnconnectedPins,
  filterConfigurationValidation,
  filterPipelineValidation,
  filterGraphValidation,
  parseComprehensiveCircuitValidation
};